import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Video,
  UserRound,
  Layers,
  BookText,
  Atom,
  Lightbulb,
  GraduationCap,
  Archive,
  Settings,
  Blocks,
  Clapperboard,
} from "lucide-react";
import { useThemeLogos } from "@/hooks/useThemeLogos";
import type { LucideIcon } from "lucide-react";
import { OrganizationSwitcher } from "@/components/organization/OrganizationSwitcher";
import { useSideRail } from "@/contexts/SideRailContext";
import { useEffect } from "react";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { FeatureFlags } from "@/constants/constants";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
  SidebarHeader,
  SidebarTrigger,
} from "@/components/ui/sidebar";

interface NavItem {
  title: string;
  icon: LucideIcon;
  path: string;
  featureFlag?: FeatureFlags;
}

interface NavGroup {
  label?: string;
  items: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    items: [
      { title: "Home", icon: LayoutDashboard, path: "/" },
    ],
  },
  {
    label: "Insights",
    items: [
      { title: "Sessions", icon: Video, path: "/analytics/sessions" },
      { title: "People", icon: UserRound, path: "/people" },
      { title: "Performance", icon: Atom, path: "/performance", featureFlag: FeatureFlags.SHOW_PERFORMANCE },
      { title: "Knowledge Gaps", icon: Lightbulb, path: "/knowledge-gaps" },
    ],
  },
  {
    label: "Configure",
    items: [
      { title: "Agent Versions", icon: Layers, path: "/agents" },
      { title: "Asset Library", icon: BookText, path: "/content-library" },
      { title: "Product Studio", icon: Clapperboard, path: "/demo-studio", featureFlag: FeatureFlags.SHOW_DEMO_STUDIO },
      { title: "Agent Training", icon: GraduationCap, path: "/training" },
      { title: "Knowledge Base", icon: Archive, path: "/background" },
    ],
  },
];

const footerItems: NavItem[] = [
  { title: "Settings", icon: Settings, path: "/organization-settings" },
  { title: "Integrations", icon: Blocks, path: "/integrations" },
];

export function ModernSidebar() {
  const location = useLocation();
  const { state, isMobile, setOpenMobile } = useSidebar();
  const navigate = useNavigate();
  const { toggleSideRail } = useSideRail();
  const { favicon: HobbesIcon } = useThemeLogos();
  const showDemoStudio = useFeatureFlagEnabled(FeatureFlags.SHOW_DEMO_STUDIO);
  const showPerformance = useFeatureFlagEnabled(FeatureFlags.SHOW_PERFORMANCE);

  const flagValues: Record<string, boolean | undefined> = {
    [FeatureFlags.SHOW_DEMO_STUDIO]: showDemoStudio,
    [FeatureFlags.SHOW_PERFORMANCE]: showPerformance,
  };

  const filteredNavGroups = navGroups.map(group => ({
    ...group,
    items: group.items.filter(item =>
      !item.featureFlag || flagValues[item.featureFlag]
    ),
  }));

  // Handle "/" keyboard shortcut to toggle Ask Hobbes side rail
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === '/' && !['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement).tagName)) {
        e.preventDefault();
        toggleSideRail();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [toggleSideRail]);

  const isCurrentPath = (path: string) => {
    if (path === "/") {
      return location.pathname === path;
    }
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const isFooterActive = (path: string) => {
    if (path === "/organization-settings") {
      return location.pathname.startsWith("/organization-settings") ||
        location.pathname.startsWith("/account-settings");
    }
    return isCurrentPath(path);
  };

  const handleNavClick = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="mt-2 px-3 py-2 flex flex-row items-center justify-between group-data-[collapsible=icon]:!px-2">
        <div className={`flex-1 min-w-0 ${!isMobile ? "group-data-[collapsible=icon]:hidden" : ""}`}>
          <OrganizationSwitcher />
        </div>
        <SidebarTrigger className="ml-2 text-sidebar-foreground hover:text-sidebar-accent-foreground group-data-[collapsible=icon]:ml-0" />
      </SidebarHeader>

      <SidebarContent className="px-2 py-1 group-data-[collapsible=icon]:!px-0">
        {/* Ask Hobbes button */}
        <SidebarGroup className="px-0 py-0">
          <SidebarGroupContent>
            <SidebarMenu className="gap-0.5 px-1 group-data-[collapsible=icon]:!px-0 group-data-[collapsible=icon]:items-center">
              <SidebarMenuButton
                className="w-full h-8 px-2 py-1.5 justify-start rounded-md border border-sidebar-border bg-transparent hover:bg-sidebar-accent transition-colors duration-150 text-sidebar-accent-foreground shadow-none mb-0.5"
                onClick={() => toggleSideRail()}
                tooltip={state === "collapsed" ? "Ask Hobbes" : undefined}
              >
                <div className="flex items-center gap-2">
                  <img src={HobbesIcon} alt="Hobbes" className="w-3.5 h-3.5" />
                  {(isMobile || state === "expanded") && (
                    <span className="font-medium text-[13px] text-foreground">Ask Hobbes</span>
                  )}
                </div>
                {(isMobile || state === "expanded") && (
                  <kbd className="ml-auto border border-sidebar-border text-sidebar-foreground/60 text-[10px] w-4.5 h-4.5 flex items-center justify-center rounded font-medium">
                    /
                  </kbd>
                )}
              </SidebarMenuButton>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Navigation groups */}
        {filteredNavGroups.map((group, groupIdx) => (
          <SidebarGroup key={group.label ?? groupIdx} className="px-0 py-0">
            {group.label && (
              <SidebarGroupLabel className="px-3 text-[10px] font-medium uppercase tracking-[0.08em] text-sidebar-foreground/50 group-data-[collapsible=icon]:hidden mt-4 mb-0.5">
                {group.label}
              </SidebarGroupLabel>
            )}
            <SidebarGroupContent>
              <SidebarMenu className="gap-px px-1 group-data-[collapsible=icon]:!px-0 group-data-[collapsible=icon]:items-center">
                {group.items.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild
                      isActive={isCurrentPath(item.path)}
                      className="w-full h-8 px-2 py-1.5 rounded-md transition-colors duration-150 data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground"
                      tooltip={state === "collapsed" ? item.title : undefined}
                    >
                      <Link
                        to={item.path}
                        className="w-full flex items-center gap-2 justify-start"
                        onClick={handleNavClick}
                      >
                        <item.icon
                          size={15}
                          strokeWidth={isCurrentPath(item.path) ? 2 : 1.5}
                          className={isCurrentPath(item.path)
                            ? "text-sidebar-accent-foreground"
                            : "text-sidebar-foreground"}
                        />
                        {(isMobile || state === "expanded") && (
                          <span className={`text-[13px] ${isCurrentPath(item.path) ? "font-medium text-sidebar-accent-foreground" : "text-sidebar-foreground"}`}>
                            {item.title}
                          </span>
                        )}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter className="px-2 py-3 group-data-[collapsible=icon]:!px-0 border-t border-sidebar-border">
        <SidebarGroup className="px-0 py-0">
          <SidebarGroupContent>
            <SidebarMenu className="gap-px px-1 group-data-[collapsible=icon]:!px-0 group-data-[collapsible=icon]:items-center">
              {footerItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    asChild
                    isActive={isFooterActive(item.path)}
                    className="w-full h-8 px-2 py-1.5 rounded-md transition-colors duration-150 data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground"
                    tooltip={state === "collapsed" ? item.title : undefined}
                  >
                    <Link
                      to={item.path}
                      className="w-full flex items-center gap-2 justify-start"
                      onClick={handleNavClick}
                    >
                      <item.icon
                        size={15}
                        strokeWidth={isFooterActive(item.path) ? 2 : 1.5}
                        className={isFooterActive(item.path) ? "text-sidebar-accent-foreground" : "text-sidebar-foreground"}
                      />
                      {(isMobile || state === "expanded") && (
                        <span className={`text-[13px] ${isFooterActive(item.path) ? "font-medium text-sidebar-accent-foreground" : "text-sidebar-foreground"}`}>
                          {item.title}
                        </span>
                      )}
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarFooter>
    </Sidebar>
  );
}
